import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import {
  BurndownReportDto,
  BurnupReportDto,
  DeadlineDashboardDto,
  DeadlineReportRowDto,
  DistributionReportDto,
  EmployeeDelayPerformanceDto,
  PmDashboardDetailDto,
  PmDashboardDto,
  PmDashboardWidgetDto,
  PmSummaryDto,
  ProgressReportDto,
  ProjectDashboardDto,
  ProjectDeadlineHealthDto,
  ResourceUtilizationReportDto,
  RiskReportDto,
  SprintReportDto,
  TeamWorkloadDto,
  TimeUtilizationReportDto,
  TrendReportDto,
  VelocityReportDto,
  WidgetDataDto,
  ProjectSlaDto,
  CumulativeFlowReportDto,
  CycleTimeReportDto,
  ForecastReportDto,
  BlockedIssueRowDto,
  MilestoneProgressReportDto,
  ReleaseProgressReportDto,
  ProjectCapacityDto,
  SprintHealthReportDto,
  ProductivityReportDto,
  MilestoneProgressRowDto,
  ChartPointDto,
  ThroughputReportDto,
  ThroughputWeekDto
} from './pm-reports.models';

function last30Days(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 30);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of ['items', 'data', 'results', 'rows', 'milestones', 'releases', 'employees', 'assignees', 'points']) {
      if (Array.isArray(obj[key])) return obj[key] as T[];
    }
  }
  return [];
}

function normalizeWidgetPayload(res: any): WidgetDataDto {
  const type = Number(res?.widgetType ?? res?.WidgetType ?? 0);
  const payload = res?.data ?? res?.Data ?? res;
  const base: WidgetDataDto = { widgetType: type, raw: payload };

  switch (type) {
    case 0: return { ...base, summary: payload as PmSummaryDto };
    case 1: return { ...base, burndown: payload as BurndownReportDto };
    case 2: return { ...base, burnup: payload as BurnupReportDto };
    case 3: return { ...base, velocity: payload as VelocityReportDto };
    case 4: return { ...base, workload: asArray<TeamWorkloadDto>(payload) };
    case 6: return { ...base, deadlineDashboard: payload as DeadlineDashboardDto };
    case 7: return { ...base, overdueRows: asArray<DeadlineReportRowDto>(payload) };
    case 8: return { ...base, projectHealth: payload as ProjectDeadlineHealthDto };
    case 9: return { ...base, sprintProgress: payload as WidgetDataDto['sprintProgress'] };
    case 10:
    case 11:
      return {
        ...base,
        listItems: asArray<NonNullable<WidgetDataDto['listItems']>[number]>(payload).map((x: any) => ({
          key: x.key ?? x.Key,
          summary: x.summary ?? x.Summary ?? x.message ?? x.Message,
          message: x.message ?? x.Message,
          name: x.actorName ?? x.ActorName
        }))
      };
    case 12: {
      const time = payload as any;
      const employees = asArray<any>(time?.byEmployee ?? time?.ByEmployee ?? time?.items ?? time);
      const items = employees.map((i: any) => ({
        name: i.employeeName ?? i.EmployeeName ?? i.name ?? '—',
        hours: Number(i.totalHours ?? i.TotalHours ?? i.hours ?? 0)
      }));
      return {
        ...base,
        timeLogged: {
          totalHours: Number(time?.totalHours ?? time?.TotalHours ?? items.reduce((s, i) => s + (i.hours ?? 0), 0)),
          items
        },
        chartPoints: items.map(i => ({ label: i.name ?? '—', value: i.hours ?? 0 }))
      };
    }
    case 13:
    case 14: {
      const items = asArray<{ label?: string; count?: number }>(payload?.items ?? payload);
      return {
        ...base,
        chartPoints: items.map(i => ({ label: i.label ?? '—', value: i.count ?? 0 })),
        listItems: items.map(i => ({ label: i.label, count: i.count }))
      };
    }
    case 15: {
      const risk = payload as any;
      const items = asArray<any>(risk?.items ?? risk?.issues ?? risk);
      return {
        ...base,
        riskReport: {
          high: risk?.high ?? risk?.High ?? 0,
          medium: risk?.medium ?? risk?.Medium ?? 0,
          low: risk?.low ?? risk?.Low ?? 0,
          items: items.map((x: any) => ({
            key: x.key ?? x.Key,
            summary: x.summary ?? x.Summary,
            risk: x.risk ?? x.Risk ?? x.riskLevelLabel
          }))
        }
      };
    }
    default:
      if (Array.isArray(payload)) {
        return { ...base, listItems: payload as WidgetDataDto['listItems'] };
      }
      return base;
  }
}

@Injectable({ providedIn: 'root' })
export class PmReportsApiService {
  private readonly api = inject(ApiService);
  private readonly base = 'v1/pm-analytics';

  summary(projectId?: string): Observable<PmSummaryDto> {
    return this.api.get<PmSummaryDto>(`${this.base}/summary`, projectId ? { projectId } : undefined);
  }

  deadlineDashboard(projectId?: string): Observable<DeadlineDashboardDto> {
    return this.api.get<DeadlineDashboardDto>(`${this.base}/deadlines/dashboard`, projectId ? { projectId } : undefined);
  }

  projectHealth(projectId: string): Observable<ProjectDeadlineHealthDto> {
    return this.api.get<ProjectDeadlineHealthDto>(`${this.base}/projects/${projectId}/health`);
  }

  overdueReport(projectId?: string): Observable<DeadlineReportRowDto[]> {
    return this.api.get<DeadlineReportRowDto[]>(`${this.base}/deadlines/overdue`, projectId ? { projectId } : undefined)
      .pipe(map(r => asArray<DeadlineReportRowDto>(r)));
  }

  delayReport(projectId?: string): Observable<DeadlineReportRowDto[]> {
    return this.api.get<DeadlineReportRowDto[]>(`${this.base}/deadlines/delay`, projectId ? { projectId } : undefined)
      .pipe(map(r => asArray<DeadlineReportRowDto>(r)));
  }

  employeePerformance(projectId?: string): Observable<EmployeeDelayPerformanceDto[]> {
    return this.api.get<EmployeeDelayPerformanceDto[]>(`${this.base}/deadlines/employee-performance`, projectId ? { projectId } : undefined)
      .pipe(map(r => asArray<EmployeeDelayPerformanceDto>(r)));
  }

  refreshDeadlines(projectId?: string): Observable<{ issuesUpdated: number; tasksUpdated: number }> {
    const path = projectId
      ? `${this.base}/deadlines/refresh?projectId=${encodeURIComponent(projectId)}`
      : `${this.base}/deadlines/refresh`;
    return this.api.post<{ issuesUpdated: number; tasksUpdated: number }>(path, {});
  }

  burndown(projectId: string, sprintId: string): Observable<BurndownReportDto> {
    return this.api.get<BurndownReportDto>(`${this.base}/projects/${projectId}/sprints/${sprintId}/burndown`);
  }

  burnup(projectId: string, sprintId: string): Observable<BurnupReportDto> {
    return this.api.get<BurnupReportDto>(`${this.base}/projects/${projectId}/sprints/${sprintId}/burnup`);
  }

  velocity(projectId: string, lastN = 6): Observable<VelocityReportDto> {
    return this.api.get<VelocityReportDto>(`${this.base}/projects/${projectId}/velocity`, { lastN });
  }

  workload(projectId: string): Observable<TeamWorkloadDto[]> {
    return this.api.get<TeamWorkloadDto[]>(`${this.base}/projects/${projectId}/workload`)
      .pipe(map(r => asArray<TeamWorkloadDto>(r)));
  }

  listDashboards(projectId?: string): Observable<PmDashboardDto[]> {
    return this.api.get<PmDashboardDto[]>(`${this.base}/dashboards`, projectId ? { projectId } : undefined)
      .pipe(map(r => asArray<PmDashboardDto>(r)));
  }

  getDashboard(id: string): Observable<PmDashboardDetailDto> {
    return this.api.get<PmDashboardDetailDto>(`${this.base}/dashboards/${id}`);
  }

  createDashboard(body: { name: string; projectId?: string; isDefault?: boolean }): Observable<PmDashboardDto> {
    return this.api.post<PmDashboardDto>(`${this.base}/dashboards`, body);
  }

  addWidget(dashboardId: string, body: {
    title: string;
    widgetType: number;
    configJson?: string;
  }): Observable<PmDashboardWidgetDto> {
    return this.api.post<PmDashboardWidgetDto>(`${this.base}/dashboards/${dashboardId}/widgets`, body);
  }

  deleteDashboard(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/dashboards/${id}`);
  }

  getProjectDashboard(projectId: string): Observable<ProjectDashboardDto> {
    return this.api.get<ProjectDashboardDto>(`${this.base}/projects/${projectId}/dashboard`);
  }

  getWidgetData(
    dashboardId: string,
    widgetId: string,
    params?: Record<string, string | number>
  ): Observable<WidgetDataDto> {
    return this.api.get<{ widgetType?: number; widgetTypeLabel?: string; data?: unknown } & WidgetDataDto>(
      `${this.base}/dashboards/${dashboardId}/widgets/${widgetId}/data`,
      params
    ).pipe(map(res => normalizeWidgetPayload(res)));
  }

  updateDashboard(id: string, body: { name?: string; isDefault?: boolean }): Observable<PmDashboardDto> {
    return this.api.put<PmDashboardDto>(`${this.base}/dashboards/${id}`, body);
  }

  updateWidget(
    dashboardId: string,
    widgetId: string,
    body: {
      title?: string;
      widgetType?: number;
      configJson?: string;
      positionX?: number;
      positionY?: number;
      width?: number;
      height?: number;
      sortOrder?: number;
    }
  ): Observable<PmDashboardWidgetDto> {
    return this.api.put<PmDashboardWidgetDto>(
      `${this.base}/dashboards/${dashboardId}/widgets/${widgetId}`,
      body
    );
  }

  deleteWidget(dashboardId: string, widgetId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/dashboards/${dashboardId}/widgets/${widgetId}`);
  }

  sprintReport(projectId: string, sprintId: string): Observable<SprintReportDto> {
    return this.api.get<SprintReportDto>(
      `${this.base}/projects/${projectId}/reports/sprint/${sprintId}`
    );
  }

  progressReport(projectId: string): Observable<ProgressReportDto> {
    return this.api.get<ProgressReportDto>(`${this.base}/projects/${projectId}/reports/progress`);
  }

  issueDistribution(projectId: string): Observable<DistributionReportDto> {
    return this.api.get<DistributionReportDto>(
      `${this.base}/projects/${projectId}/reports/issue-distribution`
    ).pipe(map(r => {
      const items = asArray<{ label: string; count: number; percent?: number }>(r);
      if (items.length && !(r as DistributionReportDto)?.items) {
        return { ...(r as object), items } as DistributionReportDto;
      }
      return r as DistributionReportDto;
    }));
  }

  priorityDistribution(projectId: string): Observable<DistributionReportDto> {
    return this.api.get<DistributionReportDto>(
      `${this.base}/projects/${projectId}/reports/priority-distribution`
    ).pipe(map(r => {
      const items = asArray<{ label: string; count: number; percent?: number }>(r);
      if (items.length && !(r as DistributionReportDto)?.items) {
        return { ...(r as object), items } as DistributionReportDto;
      }
      return r as DistributionReportDto;
    }));
  }

  bugTrend(projectId: string): Observable<TrendReportDto> {
    return this.api.get<any>(`${this.base}/projects/${projectId}/reports/bug-trend`).pipe(
      map(r => {
        const daily = asArray<{ date: string; opened: number; closed: number }>(
          r?.dailyTrend ?? r?.DailyTrend ?? r?.points
        );
        const points: ChartPointDto[] = daily.map(d => ({
          label: (d.date || (d as any).label || '').toString().slice(5, 10),
          value: Number(d.opened ?? d.closed ?? (d as any).value ?? 0)
        }));
        // Prefer opened+closed dual view via points of net activity
        const activityPoints = daily.length
          ? daily.map(d => ({
              label: (d.date || '').toString().slice(5, 10),
              value: Number(d.opened ?? 0) + Number(d.closed ?? 0)
            }))
          : (Array.isArray(r?.points) ? r.points as ChartPointDto[] : []);
        return {
          points: activityPoints.length ? activityPoints : points,
          openBugs: r?.openBugs ?? r?.OpenBugs ?? 0,
          closedBugs: r?.closedBugs ?? r?.ClosedBugs ?? 0,
          dailyTrend: daily,
          total: (r?.openBugs ?? 0) + (r?.closedBugs ?? 0)
        } as TrendReportDto;
      })
    );
  }

  throughput(projectId: string, weeks = 8): Observable<ThroughputReportDto> {
    return this.api.get<ThroughputReportDto>(
      `${this.base}/projects/${projectId}/reports/throughput`,
      { weeks }
    ).pipe(
      map(r => ({
        projectId: r?.projectId ?? projectId,
        weeks: r?.weeks ?? weeks,
        weekly: asArray<ThroughputWeekDto>((r as any)?.weekly ?? (r as any)?.Weekly)
      }))
    );
  }

  ensureRoleDashboards(projectId?: string): Observable<PmDashboardDto[]> {
    return this.api.post<PmDashboardDto[]>(
      `${this.base}/dashboards/presets${projectId ? `?projectId=${projectId}` : ''}`,
      {}
    );
  }

  timeUtilization(projectId: string): Observable<TimeUtilizationReportDto> {
    return this.api.get<TimeUtilizationReportDto>(`${this.base}/projects/${projectId}/reports/time`).pipe(
      map(r => {
        const byEmployee = asArray<TimeUtilizationReportDto['byEmployee'][number]>((r as any)?.byEmployee ?? (r as any)?.ByEmployee);
        const byIssue = asArray<TimeUtilizationReportDto['byIssue'][number]>((r as any)?.byIssue ?? (r as any)?.ByIssue);
        return {
          ...r,
          totalHours: r?.totalHours ?? 0,
          byEmployee,
          byIssue,
          items: r?.items ?? byEmployee.map(e => ({ label: e.employeeName, hours: e.totalHours }))
        };
      })
    );
  }

  resourceUtilization(projectId: string): Observable<ResourceUtilizationReportDto> {
    return this.api.get<ResourceUtilizationReportDto>(
      `${this.base}/projects/${projectId}/reports/resource-utilization`
    ).pipe(
      map(r => {
        const assignees = asArray<ResourceUtilizationReportDto['assignees'][number]>(
          (r as any)?.assignees ?? (r as any)?.Assignees ?? (r as any)?.items
        );
        return {
          ...r,
          assignees,
          items: r?.items ?? assignees.map(a => ({
            employeeName: a.employeeName,
            allocatedHours: a.availableHours,
            loggedHours: a.remainingStoryPoints,
            utilizationPercent: a.availableHours
              ? Math.round((a.remainingStoryPoints / Math.max(a.availableHours, 1)) * 100)
              : undefined
          }))
        };
      })
    );
  }

  riskReport(projectId: string): Observable<RiskReportDto> {
    return this.api.get<RiskReportDto>(`${this.base}/projects/${projectId}/reports/risk`).pipe(
      map(r => {
        const high = asArray<NonNullable<RiskReportDto['highRiskIssues']>[number]>((r as any)?.highRiskIssues);
        const overdue = asArray<NonNullable<RiskReportDto['overdueIssues']>[number]>((r as any)?.overdueIssues);
        const items = r?.items ?? [
          ...high.map(i => ({
            keyOrTitle: i.keyOrTitle || i.key || i.summary || '',
            riskLevel: i.riskLevelLabel || String(i.riskLevel ?? ''),
            dueDate: i.dueDate,
            statusLabel: i.statusLabel
          })),
          ...overdue.map(i => ({
            keyOrTitle: i.keyOrTitle || i.key || i.summary || '',
            riskLevel: 'Overdue',
            dueDate: i.dueDate,
            statusLabel: i.statusLabel
          }))
        ];
        return { ...r, highRiskIssues: high, overdueIssues: overdue, items };
      })
    );
  }

  capacity(projectId: string, from?: string, to?: string): Observable<ProjectCapacityDto> {
    const range = from && to ? { from, to } : last30Days();
    return this.api.get<ProjectCapacityDto>(`${this.base}/projects/${projectId}/capacity`, range).pipe(
      map(r => ({
        ...r,
        assigneeLoad: asArray<ProjectCapacityDto['assigneeLoad'][number]>(
          (r as any)?.assigneeLoad ?? (r as any)?.AssigneeLoad
        )
      }))
    );
  }

  projectSla(projectId: string): Observable<ProjectSlaDto> {
    return this.api.get<ProjectSlaDto>(`${this.base}/projects/${projectId}/sla`);
  }

  cumulativeFlow(projectId: string, from?: string, to?: string): Observable<CumulativeFlowReportDto> {
    const range = from && to ? { from, to } : last30Days();
    return this.api.get<CumulativeFlowReportDto>(`${this.base}/projects/${projectId}/reports/cfd`, range).pipe(
      map(r => {
        const points = asArray<CumulativeFlowReportDto['points'][number]>(
          (r as any)?.points ?? (r as any)?.Points ?? (r as any)?.days
        ).map(p => ({
          date: p.date,
          toDo: p.toDo ?? 0,
          inProgress: p.inProgress ?? 0,
          done: p.done ?? 0,
          statuses: p.statuses ?? [
            { label: 'To Do', count: p.toDo ?? 0 },
            { label: 'In Progress', count: p.inProgress ?? 0 },
            { label: 'Done', count: p.done ?? 0 }
          ]
        }));
        return { ...r, points, days: points };
      })
    );
  }

  cycleTime(projectId: string, from?: string, to?: string): Observable<CycleTimeReportDto> {
    const range = from && to ? { from, to } : last30Days();
    return this.api.get<CycleTimeReportDto>(`${this.base}/projects/${projectId}/reports/cycle-time`, range).pipe(
      map(r => ({
        ...r,
        distribution: asArray<CycleTimeReportDto['distribution'][number]>(
          (r as any)?.distribution ?? (r as any)?.Distribution
        )
      }))
    );
  }

  leadTime(projectId: string, from?: string, to?: string): Observable<CycleTimeReportDto> {
    const range = from && to ? { from, to } : last30Days();
    return this.api.get<CycleTimeReportDto>(`${this.base}/projects/${projectId}/reports/lead-time`, range).pipe(
      map(r => ({
        ...r,
        distribution: asArray<CycleTimeReportDto['distribution'][number]>(
          (r as any)?.distribution ?? (r as any)?.Distribution
        )
      }))
    );
  }

  forecast(projectId: string): Observable<ForecastReportDto> {
    return this.api.get<ForecastReportDto>(`${this.base}/projects/${projectId}/reports/forecast`);
  }

  blockedIssues(projectId: string): Observable<BlockedIssueRowDto[]> {
    return this.api.get<BlockedIssueRowDto[]>(`${this.base}/projects/${projectId}/reports/blocked`)
      .pipe(map(r => asArray<BlockedIssueRowDto>(r)));
  }

  milestoneProgress(projectId: string): Observable<MilestoneProgressReportDto> {
    return this.api.get<MilestoneProgressRowDto[] | MilestoneProgressReportDto>(
      `${this.base}/projects/${projectId}/reports/milestone-progress`
    ).pipe(
      map(r => {
        const milestones = asArray<MilestoneProgressRowDto>(r).map(m => ({
          ...m,
          statusLabel: m.statusLabel ?? m.status,
          completionPercent: m.completionPercent ?? (m as any).progressPercent ?? 0
        }));
        return { projectId, milestones };
      })
    );
  }

  releaseProgress(projectId: string): Observable<ReleaseProgressReportDto> {
    return this.api.get<MilestoneProgressRowDto[] | ReleaseProgressReportDto>(
      `${this.base}/projects/${projectId}/reports/release-progress`
    ).pipe(
      map(r => {
        const releases = asArray<MilestoneProgressRowDto>(r).map(m => ({
          ...m,
          statusLabel: m.statusLabel ?? m.status,
          completionPercent: m.completionPercent ?? 0
        }));
        return { projectId, releases };
      })
    );
  }

  sprintHealth(projectId: string, sprintId: string): Observable<SprintHealthReportDto> {
    return this.api.get<SprintHealthReportDto>(
      `${this.base}/projects/${projectId}/reports/sprint-health/${sprintId}`
    );
  }

  productivity(projectId: string): Observable<ProductivityReportDto> {
    return this.api.get<ProductivityReportDto>(
      `${this.base}/projects/${projectId}/reports/productivity`
    ).pipe(
      map(r => ({
        ...r,
        employees: asArray<ProductivityReportDto['employees'][number]>(
          (r as any)?.employees ?? (r as any)?.Employees
        )
      }))
    );
  }

  exportReport(projectId: string, type: string, format: 'csv' | 'xlsx' = 'csv', sprintId?: string): Observable<Blob> {
    const params: Record<string, string> = { type, format };
    if (sprintId) params['sprintId'] = sprintId;
    return this.api.downloadBlob(`${this.base}/projects/${projectId}/reports/export`, params);
  }
}
